import { Big } from 'big.js';

import { IHackathonProject, SortOption, TimestampInput } from '@/entities';
import { DOMAIN_SUFFIX, MOTES_PER_CSPR_RATE, PLATFORM, WALLET_URLS } from '@/constants';
import { HOME_PATH, WALLET_REDIRECT } from '@/app/paths';

export const encodeBase64 = (str: string): string => {
  return btoa(encodeURIComponent(decodeURIComponent(str)));
};

export const decodeBase64 = (encoded: string) => {
  return new TextDecoder().decode(Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0)));
};

export const getEncodedAuthData = (initData: string): string => {
  return encodeBase64(initData || '{}');
};

export const mapSortToStrapi = (sort: SortOption): string[] => {
  switch (sort) {
    case 'Newest':
      return ['published_on:desc', 'createdAt:desc'];
    case 'Oldest':
      return ['published_on:asc', 'createdAt:asc'];
    case 'Most popular':
      return ['round1_balance:desc'];
    case 'Least popular':
      return ['round1_balance:asc'];
    default:
      return ['published_on:desc'];
  }
};

export const openLink = (redirectLink: string) => () => {
  window.open(redirectLink, '_blank');
};

export const getThumbUrl = (projectLogo?: IHackathonProject['project_logo']) =>
  projectLogo?.formats?.thumbnail?.url ?? projectLogo?.url ?? '';

export const parseDate = (ts: TimestampInput) =>
  ts instanceof Date ? ts : ts ? new Date(ts) : undefined;

export const pad = (n: number) => String(n).padStart(2, '0');

export const formatDate = (ts?: string | number | Date) => {
  const d = ts ? new Date(ts) : null;
  if (!d || isNaN(d.getTime())) return '';

  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};

export const formatTime = (ts?: string | number | Date) => {
  const d = ts ? new Date(ts) : null;
  if (!d || isNaN(d.getTime())) return '';

  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());

  return `${hours}:${minutes}:${seconds}`;
};

export const formatMonthDay = (ts: TimestampInput, locale = undefined) => {
  const d = parseDate(ts);
  if (!d || isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(d);
};

export const getDevicePlatform = (): 'ios' | 'android' | 'web' => {
  //@ts-ignore
  const ua = navigator.userAgent || navigator.vendor || window.opera;

  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIOS) return 'ios';

  const isAndroid =
    /Android/.test(ua) &&
    !/Windows NT/.test(ua) && // exclude desktop
    !/Mac OS X/.test(ua) && // exclude macOS
    !/CrOS/.test(ua); // exclude ChromeOS

  if (isAndroid) return 'android';

  return 'web';
};

export const buildRedirectLink = (
  platform: string,
  origin: string,
  encodedData: string
): string => {
  if (platform.includes(PLATFORM.IOS)) {
    return `${WALLET_URLS.CASPER_WALLET}/download?browse=${origin}/index.html?u=${encodedData}#${HOME_PATH}`;
  }

  if (platform.includes(PLATFORM.ANDROID)) {
    return `${origin}${WALLET_REDIRECT}?u=${encodedData}`;
  }

  return '';
};

export const shouldBeRedirectedToCWM = (): boolean => {
  const isInAppBrowser = navigator.userAgent.includes('WebView CasperWalletMobile');
  const platform = getDevicePlatform();

  return !isInAppBrowser && platform !== 'web';
};

export const buildRedirectLinkToCWM = (platform: string, encodedData: string) => {
  if (platform.includes(PLATFORM.ANDROID)) {
    return `casperwallet://download?browse=${window.location.origin}/index.html#/app?u=${encodedData}`;
  }
  return `${WALLET_URLS.CASPER_WALLET}/download?browse=${window.location.origin}/index.html#/app?u=${encodedData}`;
};

export const motesToCSPR = (motes: string): string => {
  return Big(motes).div(MOTES_PER_CSPR_RATE).toString();
};

export const formatFansBadge = (name: string, maxLabelLength = 8) => {
  if (!name.endsWith(DOMAIN_SUFFIX)) return name;
  const label = name.slice(0, -DOMAIN_SUFFIX.length);
  if (label.length <= maxLabelLength) return name;
  return `${label.slice(0, maxLabelLength)}….${DOMAIN_SUFFIX.slice(1)}`;
};

export const getErrorMessage = (e: unknown, fallback = 'Something went wrong'): string => {
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message;
  const maybe = (e as any)?.response?.data?.message ?? (e as any)?.message;
  return typeof maybe === 'string' && maybe.length > 0 ? maybe : fallback;
};

export const isValidSubname = (name: string): boolean => {
  // This pattern ensures:
  // - The name must start with a lowercase letter (a-z) or a digit (0-9).
  // - If the name is longer than one character, the subsequent characters can be
  //   lowercase letters, digits, or hyphens, but it must end with a letter or digit.
  // - Hyphens are not allowed at the beginning or end.
  // - Minimum length of 1 is implicitly handled.
  const validSegmentPattern = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

  return (
    name.length >= 1 &&
    name.length <= 63 &&
    !/\s/.test(name) &&
    validSegmentPattern.test(name) &&
    !/^-|-$/.test(name)
  );
};

